import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AttachmentList } from "@/components/Attachments";
import { Upload, Loader2, Mail, FileSignature } from "lucide-react";
import CertificateDialog from "@/components/CertificateDialog";

export const DOC_TYPES = [
  { key: "dni", label: "DNI / NIE" },
  { key: "seguridad_social", label: "Seguridad Social" },
  { key: "contrato", label: "Contrato de trabajo" },
  { key: "titulacion", label: "Titulaciones" },
  { key: "nomina", label: "Nóminas", period: true },
  { key: "registro_jornada", label: "Registro de jornada", period: true },
  { key: "parte_baja", label: "Partes de baja" },
  { key: "certificado", label: "Certificados de empresa" },
  { key: "otros", label: "Otros documentos" },
];

const MONTH_NAMES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const MONTHS = MONTH_NAMES.map((_, i) => String(i + 1).padStart(2, "0"));
const YEARS = Array.from({ length: 7 }, (_, i) => String(new Date().getFullYear() + 1 - i));

const selectCls =
  "h-9 rounded-md border border-input bg-white px-2 text-sm transition-colors hover:border-accent/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40";

function DocGroup({ type, employeeId, docs, isAdmin, onChanged }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [month, setMonth] = useState(MONTHS[new Date().getMonth()]);
  const [year, setYear] = useState(String(new Date().getFullYear()));

  const notifyAll = async () => {
    const ids = docs.filter((d) => !d.notified_at).map((d) => d.id);
    if (!ids.length) return;
    setNotifying(true);
    try {
      const r = await api.post("/documents/notify", { file_ids: ids });
      if (r.data.sent) toast.success(`${r.data.sent} aviso(s) enviados al trabajador`);
      (r.data.skipped || []).forEach((s) => toast.error(s));
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudieron enviar los avisos"); }
    finally { setNotifying(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("doc_type", type.key);
    fd.append("period", type.period ? `${year}-${month}` : "");
    fd.append("file", file);
    try {
      await api.post(`/employees/${employeeId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${type.label}: archivo añadido`);
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo subir el archivo"); }
    finally { setBusy(false); }
  };

  const sorted = [...docs].sort((a, b) => (b.period || b.created_at || "").localeCompare(a.period || a.created_at || ""));

  return (
    <section className="py-5 first:pt-0 border-b last:border-0" data-testid={`doc-group-${type.key}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h4 className="text-sm font-semibold tracking-tight">{type.label}</h4>
          <p className="text-xs text-muted-foreground mt-0.5">
            {sorted.length ? `${sorted.length} archivo${sorted.length > 1 ? "s" : ""}` : "Sin documentos"}
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            {type.period && docs.some((d) => !d.notified_at) && (
              <Button type="button" size="sm" variant="ghost" disabled={notifying} onClick={notifyAll}
                data-testid={`doc-notify-${type.key}`}>
                {notifying ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Mail className="h-4 w-4 mr-1.5" />} Avisar
              </Button>
            )}
            {type.period && (
              <>
                <select className={selectCls} value={month} onChange={(e) => setMonth(e.target.value)}
                  aria-label="Mes" data-testid={`doc-month-${type.key}`}>
                  {MONTHS.map((m, i) => <option key={m} value={m}>{MONTH_NAMES[i]}</option>)}
                </select>
                <select className={selectCls} value={year} onChange={(e) => setYear(e.target.value)}
                  aria-label="Año" data-testid={`doc-year-${type.key}`}>
                  {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </>
            )}
            <input ref={ref} type="file" accept="application/pdf,image/*" className="hidden" onChange={upload}
              data-testid={`doc-input-${type.key}`} />
            <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => ref.current?.click()}
              data-testid={`doc-upload-${type.key}`}>
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />} Subir
            </Button>
          </div>
        )}
      </div>

      {sorted.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {type.period
            ? sorted.map((d) => (
                <div key={d.id} className="flex items-center gap-3">
                  <span className="w-16 shrink-0 font-mono text-xs text-muted-foreground">{d.period || "—"}</span>
                  <div className="min-w-0 flex-1"><AttachmentList files={[d]} isAdmin={isAdmin} onDeleted={onChanged} /></div>
                  {d.notified_at && <span className="shrink-0 text-[11px] text-emerald-700" title="Trabajador avisado por correo">avisado</span>}
                </div>
              ))
            : <AttachmentList files={sorted} isAdmin={isAdmin} onDeleted={onChanged} />}
        </div>
      )}
    </section>
  );
}

export default function EmployeeDocuments({ employeeId, isAdmin, employee }) {
  const [docs, setDocs] = useState([]);
  const [certOpen, setCertOpen] = useState(false);

  const load = () => {
    if (!employeeId) return;
    api.get(`/employees/${employeeId}/documents`).then((r) => setDocs(r.data)).catch(() => {});
  };
  useEffect(load, [employeeId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!employeeId)
    return <p className="py-10 text-center text-sm text-muted-foreground">Guarda la ficha para poder añadir documentos.</p>;

  return (
    <div data-testid="employee-documents">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">PDF o fotografía, hasta 10 MB por archivo. Solo el administrador puede subir o eliminar.</p>
        {isAdmin && (
          <Button type="button" size="sm" variant="outline" onClick={() => setCertOpen(true)} data-testid="open-certificate-dialog">
            <FileSignature className="h-4 w-4 mr-1.5" /> Generar certificado
          </Button>
        )}
      </div>
      <div className="mt-4">
        {DOC_TYPES.map((t) => (
          <DocGroup key={t.key} type={t} employeeId={employeeId} isAdmin={isAdmin}
            docs={docs.filter((d) => d.doc_type === t.key)} onChanged={load} />
        ))}
      </div>
      <CertificateDialog open={certOpen} employee={employee || { id: employeeId, name: "" }}
        onClose={() => setCertOpen(false)} onCreated={load} />
    </div>
  );
}
